import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import { CenecLoadingBlock } from './CenecSpinner';
import { exportBatterySummaryPdf } from './batterySummaryPdf';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const PANEL_BG       = '#181b1f';
const PANEL_BG_PLOT  = '#111217';
const PANEL_BORDER   = 'rgba(255,255,255,0.07)';
const PANEL_GRID     = 'rgba(255,255,255,0.08)';
const PANEL_TEXT     = '#d8d9da';
const PANEL_TEXT_DIM = '#9da0a4';
const COLOR_GREEN  = '#73BF69';
const COLOR_RED    = '#E02F44';
const COLOR_ORANGE = '#FF780A';
const COLOR_BLUE   = '#5794F2';
const COLOR_PURPLE = '#B877D9';

const RANGES = [
  { key: '1h',  label: '1 hour'  },
  { key: '6h',  label: '6 hours' },
  { key: '24h', label: '24 hours'},
  { key: '7d',  label: '7 days'  },
  { key: '30d', label: 'Month'   },
  { key: '1y',  label: 'Year'    },
];

const PERIODS = [
  { key: 'day',   label: 'Per day'   },
  { key: 'month', label: 'Per month' },
  { key: 'year',  label: 'Per year'  },
];

// Initial SOC is fixed at 50% — every simulation starts at the same baseline.
const INITIAL_SOC_PCT = 50;

// Eskom's instantaneous-reserve settings — Certification and Performance
// Monitoring of Generation Reserves, 240-110150430 Rev 3 (Aug 2021) §4.1.2 and
// §4.2.2, per Grid Code GR6. Dead-band ±150 mHz; full activation required at
// or beyond the 49.75 / 50.25 Hz target frequencies. The 75 % point at
// ±200 mHz is a scenario setting, not a code requirement — Range 2 asks for a
// proportional response without prescribing its shape.
const ESKOM_CURVE = {
  mode: 'linear',
  low:  [{ hz: 49.85, pct: 0 }, { hz: 49.80, pct: 75 }, { hz: 49.75, pct: 100 }],
  high: [{ hz: 50.15, pct: 0 }, { hz: 50.20, pct: 75 }, { hz: 50.25, pct: 100 }],
};

// A breakpoint sets the output *at* its frequency. Proportional then ramps
// between breakpoints — each segment is a droop of its own, which is the shape
// the grid code describes. Discrete holds instead, for modelling a relay-style
// response that switches in fixed blocks; it moves far less energy through the
// band, so it is a scenario, not the default.
const CURVE_MODES = [
  { key: 'linear', label: 'Proportional',
    hint: 'ramp between breakpoints, reaching each one at its frequency' },
  { key: 'step',   label: 'Discrete steps',
    hint: 'hold each breakpoint until the next is reached' },
];

const cloneCurve = (c) => ({
  mode: c.mode,
  low:  c.low.map(p  => ({ ...p })),
  high: c.high.map(p => ({ ...p })),
});

// Points ordered outward from nominal: `low` falling, `high` rising.
function orderedSide(points, side) {
  const dir = side === 'high' ? 1 : -1;
  return [...points].sort((a, b) => dir * (a.hz - b.hz));
}

// Fraction of P_max for a deviation past the dead-band edge. Mirrors
// battery_sim._response_fraction so the preview cannot disagree with the run.
function responseFraction(dev, pts, dir, mode) {
  const xs = pts.map(p => dir * (p.hz - pts[0].hz));
  const ys = pts.map(p => p.pct / 100);
  if (mode === 'step') {
    let i = 0;
    for (let k = 0; k < xs.length; k++) if (dev >= xs[k] - 1e-12) i = k;
    return ys[i];
  }
  if (dev <= xs[0]) return ys[0];
  for (let k = 1; k < xs.length; k++) {
    if (dev <= xs[k]) {
      const span = xs[k] - xs[k - 1];
      const t = span > 0 ? (dev - xs[k - 1]) / span : 0;
      return ys[k - 1] + t * (ys[k] - ys[k - 1]);
    }
  }
  return ys[ys.length - 1];
}

// Signed regulation power at a frequency, in kW. Positive = absorbing.
function evalCurveKw(f, curve, maxKw) {
  const low  = orderedSide(curve.low,  'low');
  const high = orderedSide(curve.high, 'high');
  const frLow  = responseFraction(Math.max(0, low[0].hz - f),  low,  -1, curve.mode);
  const frHigh = responseFraction(Math.max(0, f - high[0].hz), high, +1, curve.mode);
  return (frHigh - frLow) * maxKw;
}

// Same rules the backend enforces, applied before the POST so the user sees
// the problem next to the field rather than as a 400.
function validateCurve(curve) {
  for (const [side, label] of [['low', 'Under-frequency'], ['high', 'Over-frequency']]) {
    const pts = orderedSide(curve[side], side);
    if (pts.length < 2) return `${label}: needs the dead-band edge and at least one breakpoint.`;
    const dir = side === 'high' ? 1 : -1;
    for (const p of pts) {
      if (!Number.isFinite(p.hz) || !Number.isFinite(p.pct)) return `${label}: every breakpoint needs a frequency and a percentage.`;
      if (p.pct < 0 || p.pct > 100) return `${label}: ${p.pct} % is outside 0–100 %.`;
      if (p.hz < 40 || p.hz > 60)   return `${label}: ${p.hz} Hz is outside 40–60 Hz.`;
    }
    for (let k = 1; k < pts.length; k++) {
      if (dir * (pts[k].hz - pts[k - 1].hz) <= 1e-9) {
        return `${label}: two breakpoints sit at ${pts[k].hz} Hz.`;
      }
    }
  }
  if (orderedSide(curve.high, 'high')[0].hz <= orderedSide(curve.low, 'low')[0].hz) {
    return 'Upper dead-band edge must be above the lower one.';
  }
  return null;
}

const DEFAULT_CONFIG = {
  response_curve: cloneCurve(ESKOM_CURVE),
  max_power_kw: 100000, duration_hours: 4,
  eff_charge: 0.95, eff_discharge: 0.95,
  low_threshold_pct: 20, high_threshold_pct: 95,
  ext_low_charge_kw: 50000, ext_high_discharge_kw: 50000,
  source_site: 'bloemfontein',
};

const plotConfig = { responsive: true, displayModeBar: false };

function isoLocal(d) {
  if (!d) return '';
  const pad = n => String(n).padStart(2, '0');
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function fmtKwh(v) {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e6) return (v/1e6).toFixed(2) + ' GWh';
  if (abs >= 1e3) return (v/1e3).toFixed(2) + ' MWh';
  return v.toFixed(1) + ' kWh';
}
function fmtPct(v) {
  return v === null || v === undefined ? '—' : v.toFixed(1) + '%';
}
function fmtNum(v, dp = 2) {
  return v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(dp);
}

const Panel = ({ title, children, action, style }) => (
  <div style={{
    background: PANEL_BG, border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
    marginBottom: 12, overflow: 'hidden', ...style,
  }}>
    <div style={{
      padding: '8px 12px', fontSize: 12, fontWeight: 500, color: PANEL_TEXT,
      borderBottom: `1px solid ${PANEL_BORDER}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span>{title}</span>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const InputRow = ({ label, value, onChange, unit, type='number', step, min, max, hint }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderBottom: `1px solid ${PANEL_BORDER}` }}>
    <div style={{ flex: '0 0 50%', fontSize: 11, color: PANEL_TEXT }}>
      {label}
      {hint && <div style={{ fontSize: 10, color: PANEL_TEXT_DIM, marginTop: 1 }}>{hint}</div>}
    </div>
    <input
      type={type} value={value} step={step} min={min} max={max}
      onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      style={{
        flex: '1 1 auto', minWidth: 0, background: '#0f1116', color: PANEL_TEXT,
        border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
        padding: '3px 6px', fontFamily: 'inherit', fontSize: 11,
      }}
    />
    {unit && <span style={{ flex: '0 0 30px', fontSize: 10, color: PANEL_TEXT_DIM, textAlign: 'right' }}>{unit}</span>}
  </div>
);

// f–P preview of the configured curve.
//
// Drawn as inline SVG rather than with Plotly: this plot is re-created on every
// keystroke in the breakpoint table, and react-plotly.js latched a zero height
// on alternate renders when the layout object was reused that way. An SVG has
// no resize or mutation semantics to get out of step with.
//
// The curve itself is sampled through evalCurveKw — the same arithmetic the
// simulator runs — so the picture cannot disagree with the model. Dense
// sampling rather than exact vertices keeps stepped and linear on one path;
// at four samples per rendered pixel a step edge reads as vertical.
const CurvePreview = ({ curve, maxKw }) => {
  const W = 620, H = 200, padL = 56, padR = 14, padT = 12, padB = 34;

  const low  = orderedSide(curve.low,  'low');
  const high = orderedSide(curve.high, 'high');
  const fMin = low[low.length - 1].hz   - 0.06;
  const fMax = high[high.length - 1].hz + 0.06;
  const maxMw = Math.max(maxKw / 1000, 1e-6);

  const px = f  => padL + (f - fMin) / (fMax - fMin) * (W - padL - padR);
  const py = mw => padT + (1 - (mw + maxMw) / (2 * maxMw)) * (H - padT - padB);

  const N = (W - padL - padR) * 4;
  let d = '';
  for (let i = 0; i <= N; i++) {
    const f = fMin + (fMax - fMin) * i / N;
    d += `${i ? 'L' : 'M'}${px(f).toFixed(2)},${py(evalCurveKw(f, curve, maxKw) / 1000).toFixed(2)}`;
  }

  // Label every breakpoint that has room; always keep the outermost ones.
  const ticks = [...low, ...high].map(p => p.hz).sort((a, b) => a - b);
  let lastLabelX = -Infinity;
  const labelled = ticks.map((hz) => {
    const at = px(hz);
    const show = at - lastLabelX >= 30;
    if (show) lastLabelX = at;
    return { hz, at, show };
  });

  const fmtHz = hz => String(Number(hz.toFixed(3)));
  const yTicks = [1, 0.5, 0, -0.5, -1].map(k => k * maxMw);

  // height goes through CSS, not the SVG attribute: as an attribute "auto" is
  // not a length and the browser rejects it.
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%"
         style={{ display: 'block', height: 'auto', background: PANEL_BG_PLOT }}
         fill={PANEL_TEXT_DIM} fontFamily='Inter, "Work Sans", sans-serif' fontSize="9">
      {/* dead-band */}
      <rect x={px(low[0].hz)} y={padT} width={Math.max(0, px(high[0].hz) - px(low[0].hz))}
            height={H - padT - padB} fill="rgba(255,255,255,0.035)" />
      {/* y grid + labels */}
      {yTicks.map((mw, i) => (
        <g key={`y${i}`}>
          <line x1={padL} x2={W - padR} y1={py(mw)} y2={py(mw)}
                stroke={mw === 0 ? PANEL_BORDER : PANEL_GRID} strokeWidth="1" />
          <text x={padL - 6} y={py(mw) + 3} textAnchor="end" fill={PANEL_TEXT_DIM}>
            {mw === 0 ? '0' : mw.toFixed(mw % 1 ? 1 : 0)}
          </text>
        </g>
      ))}
      {/* breakpoint ticks + labels */}
      {labelled.map(({ hz, at, show }, i) => (
        <g key={`x${i}`}>
          <line x1={at} x2={at} y1={padT} y2={H - padB} stroke={PANEL_GRID} strokeWidth="1" />
          {show && (
            <text x={at} y={H - padB + 12} textAnchor="middle" fill={PANEL_TEXT_DIM}>{fmtHz(hz)}</text>
          )}
        </g>
      ))}
      {/* frame */}
      <rect x={padL} y={padT} width={W - padL - padR} height={H - padT - padB}
            fill="none" stroke={PANEL_BORDER} strokeWidth="1" />
      {/* the curve */}
      <path d={d} fill="none" stroke={COLOR_ORANGE} strokeWidth="1.6"
            strokeLinejoin="round" shapeRendering="geometricPrecision" />
      {/* axis titles */}
      <text x={W / 2} y={H - 4} textAnchor="middle" fill={PANEL_TEXT_DIM}>Grid frequency (Hz)</text>
      <text x={12} y={(H - padB + padT) / 2} textAnchor="middle" fill={PANEL_TEXT_DIM}
            transform={`rotate(-90 12 ${(H - padB + padT) / 2})`}>+ absorb / − inject (MW)</text>
    </svg>
  );
};

const cellInput = {
  width: '100%', minWidth: 0, background: '#0f1116', color: PANEL_TEXT,
  border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
  padding: '3px 6px', fontFamily: 'inherit', fontSize: 11,
};

// One side of the response curve. Row 0 is the dead-band edge — its frequency
// is editable, its output is 0 % by definition and is not.
const CurveSide = ({ title, hint, side, points, onChange }) => {
  const setPoint = (i, field, value) =>
    onChange(points.map((p, k) => (k === i ? { ...p, [field]: value } : p)));

  const addPoint = () => {
    const ordered = orderedSide(points, side);
    const last = ordered[ordered.length - 1];
    const dir = side === 'high' ? 1 : -1;
    onChange([...points, { hz: Number((last.hz + dir * 0.05).toFixed(3)), pct: 100 }]);
  };

  const removePoint = (i) => onChange(points.filter((_, k) => k !== i));

  return (
    <div style={{ borderBottom: `1px solid ${PANEL_BORDER}` }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px 2px',
      }}>
        <div>
          <div style={{ fontSize: 11, color: PANEL_TEXT }}>{title}</div>
          <div style={{ fontSize: 10, color: PANEL_TEXT_DIM, marginTop: 1 }}>{hint}</div>
        </div>
        <button onClick={addPoint} title="Add a breakpoint"
          style={{
            background: 'transparent', color: PANEL_TEXT_DIM,
            border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
            padding: '1px 7px', fontSize: 11, cursor: 'pointer', lineHeight: 1.6,
            whiteSpace: 'nowrap',
          }}>+ point</button>
      </div>
      <div style={{ padding: '2px 10px 8px' }}>
        <div style={{ display: 'flex', gap: 6, fontSize: 10, color: PANEL_TEXT_DIM, padding: '2px 0' }}>
          <span style={{ flex: '0 0 70px' }}>Breakpoint</span>
          <span style={{ flex: '1 1 auto' }}>Frequency (Hz)</span>
          <span style={{ flex: '0 0 56px' }}>% P<sub>max</sub></span>
          <span style={{ flex: '0 0 18px' }} />
        </div>
        {points.map((p, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '2px 0' }}>
            <span style={{ flex: '0 0 70px', fontSize: 10, color: PANEL_TEXT_DIM }}>
              {i === 0 ? 'Dead-band' : `Point ${i}`}
            </span>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <input type="number" step={0.005} value={p.hz}
                onChange={e => setPoint(i, 'hz', Number(e.target.value))}
                style={cellInput} />
            </div>
            <div style={{ flex: '0 0 56px' }}>
              <input type="number" step={5} min={0} max={100}
                value={i === 0 ? 0 : p.pct} disabled={i === 0}
                onChange={e => setPoint(i, 'pct', Number(e.target.value))}
                style={{ ...cellInput, opacity: i === 0 ? 0.45 : 1 }} />
            </div>
            <button onClick={() => removePoint(i)} disabled={i === 0 || points.length <= 2}
              title={i === 0 ? 'The dead-band edge cannot be removed' : 'Remove this breakpoint'}
              style={{
                flex: '0 0 18px', background: 'transparent',
                color: (i === 0 || points.length <= 2) ? '#3a3d42' : PANEL_TEXT_DIM,
                border: 'none', fontSize: 13, lineHeight: 1,
                cursor: (i === 0 || points.length <= 2) ? 'not-allowed' : 'pointer', padding: 0,
              }}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Width in milliseconds for each preset, used when the user clicks a preset
// while the navigator is in free-positioning mode.
const PRESET_WIDTH_MS = {
  '1h':  3600_000,
  '6h':  6 * 3600_000,
  '24h': 24 * 3600_000,
  '7d':  7 * 86_400_000,
  '30d': 30 * 86_400_000,
  '1y':  365 * 86_400_000,
};

const BatterySimulationView = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [availableRange, setAvailableRange] = useState(null);
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [simExists, setSimExists] = useState(false);
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);     // full-range SOC for navigator
  const [windowFrom, setWindowFrom] = useState(null); // ISO string — viewing window
  const [windowTo, setWindowTo] = useState(null);
  const [range, setRange] = useState('24h');          // preset width key
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState(null);
  const [period, setPeriod] = useState('day');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null); // { pct, message }
  const [error, setError] = useState(null);

  const fetchState = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/battery/available-range`);
      if (r.ok) setAvailableRange(await r.json());
      const s = await fetch(`${API}/api/battery/state`);
      if (s.ok) {
        const json = await s.json();
        setSimExists(json.simulation_rows > 0);
        if (json.config) {
          setConfig(c => ({
            ...c,
            // The backend always returns a curve: a config stored before the
            // curve existed comes back as its equivalent two-point linear ramp.
            response_curve: json.config.response_curve
              ? cloneCurve(json.config.response_curve)
              : c.response_curve,
            max_power_kw: Number(json.config.max_power_kw),
            duration_hours: Number(json.config.duration_hours),
            eff_charge: Number(json.config.eff_charge),
            eff_discharge: Number(json.config.eff_discharge),
            low_threshold_pct: Number(json.config.low_threshold_pct),
            high_threshold_pct: Number(json.config.high_threshold_pct),
            ext_low_charge_kw: Number(json.config.ext_low_charge_kw),
            ext_high_discharge_kw: Number(json.config.ext_high_discharge_kw),
            source_site: json.config.source_site,
          }));
          setTimeFrom(isoLocal(json.config.time_from));
          setTimeTo(isoLocal(json.config.time_to));
        }
      }
    } catch (e) { /* */ }
  }, []);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Seed the time window once, when the available range first arrives.
  //
  // This must not re-run on timeFrom/timeTo changes. A <input type="datetime-local">
  // reports value '' for any partially-typed value, which happens on nearly every
  // keystroke while a date is entered segment by segment. With those in the
  // dependency array, an "if empty, refill" effect fires mid-edit and overwrites
  // what the user is typing with the default window.
  const windowSeeded = useRef(false);

  useEffect(() => {
    if (windowSeeded.current) return;
    if (!availableRange) return;
    const site = availableRange.sites.find(s => s.site === config.source_site);
    if (!site) return;
    windowSeeded.current = true;
    setTimeTo(prev => prev || isoLocal(site.last_ts));
    setTimeFrom(prev => {
      if (prev) return prev;
      const t = new Date(site.last_ts);
      t.setDate(t.getDate() - 1);
      return isoLocal(t);
    });
  }, [availableRange, config.source_site]);

  const dateRangeOk = timeFrom && timeTo && new Date(timeFrom) < new Date(timeTo);

  const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const setCurve = (patch) =>
    setConfig(c => ({ ...c, response_curve: { ...c.response_curve, ...patch } }));

  const curveError = useMemo(() => validateCurve(config.response_curve), [config.response_curve]);

  const onRun = async () => {
    if (curveError) { setError(curveError); return; }
    setError(null); setRunning(true);
    setProgress({ pct: 0, message: 'submitting…' });
    try {
      const body = { ...config, initial_soc_pct: INITIAL_SOC_PCT, time_from: timeFrom, time_to: timeTo };
      const r = await fetch(`${API}/api/battery/run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'simulation failed');
      const jobId = json.job_id;
      if (!jobId) throw new Error('backend did not return a job_id');

      // Poll the job until done or error. 2 s cadence — generous for the user,
      // gentle on the backend.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise(res => setTimeout(res, 2000));
        const jr = await fetch(`${API}/api/battery/job/${jobId}`);
        if (!jr.ok) throw new Error(`job ${jobId} not found on backend`);
        const job = await jr.json();
        setProgress({ pct: Number(job.progress || 0), message: String(job.message || '') });
        if (job.status === 'done')  break;
        if (job.status === 'error') throw new Error(job.error || 'simulation failed');
      }
      await fetchState();
      await Promise.all([loadData(), loadSummary()]);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete the current simulation and its config?')) return;
    setError(null);
    try {
      const r = await fetch(`${API}/api/battery/simulation`, { method: 'DELETE' });
      if (!r.ok) throw new Error('delete failed');
      setData(null); setSummary(null);
      await fetchState();
    } catch (e) { setError(String(e.message || e)); }
  };

  const loadOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/battery/simulation/overview`);
      if (r.ok) {
        const json = await r.json();
        setOverview(json);
        // First load: seed the detail window to the most recent `range` preset.
        if (!windowFrom || !windowTo) {
          const lastMs  = new Date(json.last_ts).getTime();
          const widthMs = PRESET_WIDTH_MS[range] || PRESET_WIDTH_MS['24h'];
          const firstMs = Math.max(new Date(json.first_ts).getTime(), lastMs - widthMs);
          setWindowFrom(new Date(firstMs).toISOString());
          setWindowTo(new Date(lastMs).toISOString());
        }
      }
    } catch (e) {}
  }, [range, windowFrom, windowTo]);

  const loadData = useCallback(async () => {
    if (!windowFrom || !windowTo) return;
    try {
      const u = `${API}/api/battery/simulation/data?time_from=${encodeURIComponent(windowFrom)}&time_to=${encodeURIComponent(windowTo)}`;
      const r = await fetch(u);
      if (r.ok) setData(await r.json());
    } catch (e) {}
  }, [windowFrom, windowTo]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/battery/summary?period=${period}`);
      if (r.ok) setSummary(await r.json());
    } catch (e) {}
  }, [period]);

  // The PDF exporter opens the save-location picker before it builds anything,
  // so nothing may be awaited here first — that would spend the click's user
  // activation and Chrome would refuse to show the picker.
  const onExportPdf = useCallback(async () => {
    if (!summary || exporting) return;
    setExporting(true);
    setExportMsg(null);
    try {
      const res = await exportBatterySummaryPdf(summary);
      setExportMsg(res.cancelled ? null : `Saved ${res.filename}`);
    } catch (e) {
      setExportMsg(`Export failed — ${e.message}`);
    } finally {
      setExporting(false);
    }
  }, [summary, exporting]);

  useEffect(() => { if (simExists) loadOverview(); }, [simExists, loadOverview]);
  useEffect(() => { if (simExists) loadData(); },     [simExists, loadData]);
  useEffect(() => { if (simExists) loadSummary(); },  [simExists, loadSummary]);

  // Preset click → recentre window with the new width, anchored at current window's end
  // (or at the latest sample if no window yet).
  const onPresetChange = (key) => {
    setRange(key);
    const widthMs = PRESET_WIDTH_MS[key];
    if (!overview || !widthMs) return;
    const lastMs  = windowTo  ? new Date(windowTo).getTime() : new Date(overview.last_ts).getTime();
    const firstMs = Math.max(new Date(overview.first_ts).getTime(), lastMs - widthMs);
    setWindowFrom(new Date(firstMs).toISOString());
    setWindowTo(new Date(lastMs).toISOString());
  };

  // Navigator: handle Plotly's relayout event when user moves the slider/zooms
  const onNavRelayout = (ev) => {
    const r0 = ev['xaxis.range[0]'] || ev['xaxis.range']?.[0];
    const r1 = ev['xaxis.range[1]'] || ev['xaxis.range']?.[1];
    if (r0 && r1) {
      const f = new Date(r0).toISOString();
      const t = new Date(r1).toISOString();
      if (f !== windowFrom || t !== windowTo) {
        setWindowFrom(f); setWindowTo(t);
      }
    }
  };

  // ============ One linked figure with 4 stacked subplots ============
  // xaxis4 (bottom) has the rangeslider; xaxis/xaxis2/xaxis3 match it so all
  // four subplots zoom/pan together.
  const combinedTraces = useMemo(() => {
    if (!data) return [];
    const s = data.series;
    return [
      { x: s.ts, y: s.freq_hz, xaxis: 'x', yaxis: 'y',
        name: 'Frequency', mode: 'lines',
        line: { color: COLOR_PURPLE, width: 1.2 },
        hovertemplate: '%{x|%Y-%m-%d %H:%M:%S}<br>%{y:.3f} Hz<extra></extra>' },
      { x: s.ts, y: s.p_freqreg_kw.map(v => v/1000), xaxis: 'x2', yaxis: 'y2',
        name: 'Reg power', mode: 'lines',
        line: { color: COLOR_ORANGE, width: 1.2 },
        hovertemplate: '%{x|%Y-%m-%d %H:%M:%S}<br>%{y:.2f} MW<extra></extra>' },
      // SOC: min/max band (transparent fill) + mean line
      { x: s.ts, y: s.soc_pct_min, xaxis: 'x3', yaxis: 'y3', mode: 'lines',
        line: { color: 'rgba(115,191,105,0)', width: 0 }, showlegend: false, hoverinfo: 'skip' },
      { x: s.ts, y: s.soc_pct_max, xaxis: 'x3', yaxis: 'y3', mode: 'lines',
        line: { color: 'rgba(115,191,105,0)', width: 0 },
        fill: 'tonexty', fillcolor: 'rgba(115,191,105,0.15)',
        showlegend: false, hoverinfo: 'skip' },
      { x: s.ts, y: s.soc_pct, xaxis: 'x3', yaxis: 'y3',
        name: 'SOC', mode: 'lines',
        line: { color: COLOR_GREEN, width: 1.5 },
        hovertemplate: '%{x|%Y-%m-%d %H:%M:%S}<br>%{y:.1f}%<extra></extra>' },
      { x: s.ts, y: s.p_external_kw.map(v => v/1000), xaxis: 'x4', yaxis: 'y4',
        name: 'External power', mode: 'lines',
        line: { color: COLOR_BLUE, width: 1.2 },
        fill: 'tozeroy', fillcolor: 'rgba(87,148,242,0.18)',
        hovertemplate: '%{x|%Y-%m-%d %H:%M:%S}<br>%{y:.2f} MW<extra></extra>' },
    ];
  }, [data]);

  // Vertical slot layout (Plotly y-domain is bottom→top, so slot 1 is at top).
  // Each row gets its own y-axis; x-axes share via matches='x4'.
  const combinedLayout = useMemo(() => ({
    paper_bgcolor: PANEL_BG, plot_bgcolor: PANEL_BG_PLOT,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif', size: 11 },
    height: 1080,
    margin: { t: 30, r: 16, b: 80, l: 64 },
    showlegend: false,
    hovermode: 'x unified',
    hoverlabel: { bgcolor: PANEL_BG, bordercolor: PANEL_BORDER, font: { color: PANEL_TEXT } },
    uirevision: range,

    // y-axes — each row, top to bottom
    yaxis:  { domain: [0.79, 1.00], title: { text: 'Frequency (Hz)', font: { size: 11, color: PANEL_TEXT_DIM } },
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: false,
              tickfont: { color: PANEL_TEXT_DIM, size: 10 }, tickformat: '.3f' },
    yaxis2: { domain: [0.55, 0.76], title: { text: 'Reg power (MW)', font: { size: 11, color: PANEL_TEXT_DIM } },
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: true, zerolinecolor: PANEL_BORDER,
              tickfont: { color: PANEL_TEXT_DIM, size: 10 } },
    yaxis3: { domain: [0.31, 0.52], title: { text: 'SOC (%)', font: { size: 11, color: PANEL_TEXT_DIM } },
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: false,
              range: [0, 100], tickfont: { color: PANEL_TEXT_DIM, size: 10 } },
    yaxis4: { domain: [0.07, 0.28], title: { text: 'External (MW)', font: { size: 11, color: PANEL_TEXT_DIM } },
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER, zeroline: true, zerolinecolor: PANEL_BORDER,
              tickfont: { color: PANEL_TEXT_DIM, size: 10 } },

    // x-axes — bottom one has the slider, others match it
    xaxis:  { type: 'date', anchor: 'y',  matches: 'x4', showticklabels: false,
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
              showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across' },
    xaxis2: { type: 'date', anchor: 'y2', matches: 'x4', showticklabels: false,
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
              showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across' },
    xaxis3: { type: 'date', anchor: 'y3', matches: 'x4', showticklabels: false,
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
              showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across' },
    xaxis4: { type: 'date', anchor: 'y4',
              gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
              tickfont: { color: PANEL_TEXT_DIM, size: 10 },
              showspikes: true, spikecolor: PANEL_TEXT_DIM, spikethickness: 1, spikedash: 'dot', spikemode: 'across' },
  }), []);

  // ============ Navigator (full-range overview with rangeslider) ============
  const navTraces = useMemo(() => {
    if (!overview) return [];
    return [{
      x: overview.ts, y: overview.soc_pct,
      mode: 'lines', line: { color: COLOR_GREEN, width: 1 },
      hovertemplate: '%{x|%Y-%m-%d}<br>SOC %{y:.0f}%<extra></extra>',
    }];
  }, [overview]);

  const navLayout = useMemo(() => ({
    paper_bgcolor: PANEL_BG, plot_bgcolor: PANEL_BG_PLOT,
    font: { color: PANEL_TEXT, family: 'Inter, "Work Sans", sans-serif', size: 10 },
    height: 140,
    margin: { t: 6, r: 16, b: 4, l: 64 },
    showlegend: false,
    dragmode: 'zoom',
    xaxis: {
      type: 'date',
      range: (windowFrom && windowTo) ? [windowFrom, windowTo] : undefined,
      autorange: false,
      gridcolor: PANEL_GRID, linecolor: PANEL_BORDER,
      tickfont: { color: PANEL_TEXT_DIM, size: 10 },
      rangeslider: {
        visible: true,
        bgcolor: PANEL_BG_PLOT,
        bordercolor: PANEL_BORDER,
        borderwidth: 1,
        thickness: 0.5,
      },
    },
    yaxis: {
      gridcolor: 'rgba(0,0,0,0)', linecolor: PANEL_BORDER,
      tickfont: { color: PANEL_TEXT_DIM, size: 9 },
      range: [0, 100], ticksuffix: '%', fixedrange: true,
    },
  }), [windowFrom, windowTo]);

  // ============ Render ============
  const capacity_kwh = config.max_power_kw * config.duration_hours;
  const bloem = availableRange?.sites?.find(s => s.site === 'bloemfontein');
  const minTs = bloem?.first_ts || '';
  const maxTs = bloem?.last_ts || '';

  const runButtonDisabled = running || simExists || !dateRangeOk || !!curveError;

  return (
    <div style={{ padding: 0, color: PANEL_TEXT, fontFamily: 'Inter, "Work Sans", sans-serif' }}>
      {/* ===== Config + run controls — 3 columns side by side ===== */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 0, flexWrap: 'wrap' }}>

        {/* Group 1: Frequency response curve */}
        <div style={{ flex: '1 1 360px' }}>
          <Panel title="Frequency settings"
            action={
              <button onClick={() => setField('response_curve', cloneCurve(ESKOM_CURVE))}
                title="Dead-band ±150 mHz, full activation at 49.75 / 50.25 Hz — 240-110150430 Rev 3 §4.1.2"
                style={{
                  background: 'transparent', color: PANEL_TEXT_DIM,
                  border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
                  padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>Eskom grid code</button>
            }>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                          borderBottom: `1px solid ${PANEL_BORDER}` }}>
              <div style={{ flex: '0 0 50%', fontSize: 11, color: PANEL_TEXT }}>
                Response shape
                <div style={{ fontSize: 10, color: PANEL_TEXT_DIM, marginTop: 1 }}>
                  {CURVE_MODES.find(m => m.key === config.response_curve.mode)?.hint}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {CURVE_MODES.map(m => (
                  <button key={m.key} onClick={() => setCurve({ mode: m.key })}
                    style={{
                      background: config.response_curve.mode === m.key ? 'rgba(78,204,163,0.18)' : 'transparent',
                      color: PANEL_TEXT, border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
                      padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>{m.label}</button>
                ))}
              </div>
            </div>

            <CurveSide
              title="Over-frequency — absorb"
              hint="Charge from the grid — each point sets the output at that frequency"
              side="high"
              points={config.response_curve.high}
              onChange={pts => setCurve({ high: pts })} />

            <CurveSide
              title="Under-frequency — inject"
              hint="Discharge to the grid — each point sets the output at that frequency"
              side="low"
              points={config.response_curve.low}
              onChange={pts => setCurve({ low: pts })} />

            {curveError ? (
              <div style={{ padding: '8px 10px', fontSize: 11, color: COLOR_RED, background: 'rgba(224,47,68,0.1)' }}>
                ⚠ {curveError}
              </div>
            ) : (
              <CurvePreview curve={config.response_curve} maxKw={config.max_power_kw} />
            )}
          </Panel>
        </div>

        {/* Group 2: Battery settings */}
        <div style={{ flex: '1 1 320px' }}>
          <Panel title="Battery settings">
            <InputRow label="Max power"           value={config.max_power_kw} unit="kW" step={1000}
                      onChange={v => setField('max_power_kw', v)} />
            <InputRow label="Duration"            value={config.duration_hours} unit="h" step={0.5}
                      onChange={v => setField('duration_hours', v)}
                      hint={`Capacity = ${(capacity_kwh/1000).toFixed(1)} MWh`} />
            <InputRow label="Charge eff. (AC→cells)"    value={config.eff_charge}    step={0.01} min={0.1} max={1.0}
                      onChange={v => setField('eff_charge', v)} />
            <InputRow label="Discharge eff. (cells→AC)" value={config.eff_discharge} step={0.01} min={0.1} max={1.0}
                      onChange={v => setField('eff_discharge', v)} />
            <InputRow label="Lower SOC threshold"  value={config.low_threshold_pct} unit="%" step={1}
                      onChange={v => setField('low_threshold_pct', v)}
                      hint="Below this, recharge from grid (+3 % hysteresis)" />
            <InputRow label="Upper SOC threshold"  value={config.high_threshold_pct} unit="%" step={1}
                      onChange={v => setField('high_threshold_pct', v)}
                      hint="Above this, bleed to grid (−3 % hysteresis)" />
            <InputRow label="External recharge"   value={config.ext_low_charge_kw} unit="kW" step={1000}
                      onChange={v => setField('ext_low_charge_kw', v)} />
            <InputRow label="External bleed"      value={config.ext_high_discharge_kw} unit="kW" step={1000}
                      onChange={v => setField('ext_high_discharge_kw', v)} />
          </Panel>
        </div>

        {/* Group 3: Time window + run/delete */}
        <div style={{ flex: '1 1 320px' }}>
          <Panel title="Time window"
            action={
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={onRun} disabled={runButtonDisabled}
                  style={{
                    background: runButtonDisabled ? '#3a3d42' : 'rgba(115,191,105,0.18)',
                    color: runButtonDisabled ? PANEL_TEXT_DIM : COLOR_GREEN,
                    border: `1px solid ${runButtonDisabled ? PANEL_BORDER : 'rgba(115,191,105,0.4)'}`,
                    borderRadius: 3, padding: '3px 10px', cursor: runButtonDisabled ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                  }}>
                  {running ? 'Running…' : 'Run'}
                </button>
                <button onClick={onDelete} disabled={!simExists}
                  style={{
                    background: !simExists ? '#3a3d42' : 'rgba(224,47,68,0.18)',
                    color: !simExists ? PANEL_TEXT_DIM : COLOR_RED,
                    border: `1px solid ${!simExists ? PANEL_BORDER : 'rgba(224,47,68,0.4)'}`,
                    borderRadius: 3, padding: '3px 10px', cursor: !simExists ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                  }}>
                  Delete
                </button>
              </div>
            }>
            <div style={{ padding: '8px 10px', fontSize: 10, color: PANEL_TEXT_DIM }}>
              {bloem
                ? `Available: ${minTs?.slice(0,16).replace('T',' ')} → ${maxTs?.slice(0,16).replace('T',' ')}`
                : 'Loading…'}
              <div style={{ marginTop: 2 }}>Start SOC: fixed at {INITIAL_SOC_PCT}%</div>
            </div>
            <InputRow type="datetime-local" label="From" value={timeFrom} onChange={setTimeFrom}
                      min={isoLocal(minTs)} max={isoLocal(maxTs)} />
            <InputRow type="datetime-local" label="To"   value={timeTo}   onChange={setTimeTo}
                      min={isoLocal(minTs)} max={isoLocal(maxTs)} />
            {timeFrom && timeTo && !dateRangeOk && (
              <div style={{ padding: '6px 10px', fontSize: 11, color: COLOR_RED, background: 'rgba(224,47,68,0.1)' }}>
                ⚠ "From" must be earlier than "To".
              </div>
            )}
            {error && (
              <div style={{ padding: '6px 10px', fontSize: 11, color: COLOR_RED, background: 'rgba(224,47,68,0.1)' }}>
                {error}
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* === Results === */}
      {simExists && data && (
        <>
          <Panel
            title="Simulation results"
            action={
              <div style={{ display: 'flex', gap: 4 }}>
                {RANGES.map(r => (
                  <button key={r.key} onClick={() => onPresetChange(r.key)}
                    style={{
                      background: range === r.key ? 'rgba(78,204,163,0.18)' : 'transparent',
                      color: PANEL_TEXT, border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
                      padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                    }}>{r.label}</button>
                ))}
              </div>
            }>
            <div style={{ padding: '8px 12px', fontSize: 11, color: PANEL_TEXT_DIM }}>
              {data.point_count.toLocaleString()} points · bucket {data.bucket} ·
              window {windowFrom?.slice(0,16).replace('T',' ')} → {windowTo?.slice(0,16).replace('T',' ')}
              {' '}· Drag the slider below to navigate through the full simulation range.
            </div>

            {/* Navigator: full-range SOC with rangeslider — drag to set the viewing window */}
            {overview && (
              <div style={{ borderTop: `1px solid ${PANEL_BORDER}`, padding: '4px 0 0' }}>
                <div style={{ padding: '4px 12px 0', fontSize: 10, color: PANEL_TEXT_DIM }}>
                  Navigator · full range {overview.first_ts.slice(0,10)} → {overview.last_ts.slice(0,10)}
                </div>
                <Plot
                  data={navTraces}
                  layout={navLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: 140 }}
                  onRelayout={onNavRelayout}
                />
              </div>
            )}

            <Plot
              data={combinedTraces}
              layout={combinedLayout}
              config={plotConfig}
              style={{ width: '100%', height: 1080 }}
            />
          </Panel>

          {/* Summary table */}
          <Panel
            title="Energy summary"
            action={
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {exportMsg && (
                  <span style={{
                    fontSize: 10,
                    color: exportMsg.startsWith('Export failed') ? COLOR_RED : PANEL_TEXT_DIM,
                    marginRight: 4,
                  }}>{exportMsg}</span>
                )}
                {PERIODS.map(p => (
                  <button key={p.key} onClick={() => setPeriod(p.key)}
                    style={{
                      background: period === p.key ? 'rgba(78,204,163,0.18)' : 'transparent',
                      color: PANEL_TEXT, border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
                      padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                    }}>{p.label}</button>
                ))}
                <button onClick={onExportPdf} disabled={!summary || exporting}
                  title="Export this summary as a CENEC-branded PDF"
                  style={{
                    background: 'rgba(78,204,163,0.18)', color: COLOR_GREEN,
                    border: `1px solid ${COLOR_GREEN}55`, borderRadius: 3,
                    padding: '2px 10px', fontSize: 11, marginLeft: 6,
                    cursor: (!summary || exporting) ? 'default' : 'pointer',
                    opacity: (!summary || exporting) ? 0.5 : 1,
                  }}>{exporting ? 'Exporting…' : 'Export PDF'}</button>
              </div>
            }>
            {summary && (
              <div style={{ padding: 8 }}>
                <StatGroup label="Frequency-response duty">
                  <Stat label="Energy injected (BESS → grid)" value={fmtKwh(summary.totals.freqreg_grid_out_kwh)} />
                  <Stat label="Energy absorbed (grid → BESS)" value={fmtKwh(summary.totals.freqreg_grid_in_kwh)} />
                </StatGroup>

                <StatGroup label="SOC housekeeping — keeping the service available">
                  <Stat label="Feed / recharge (grid → BESS)" value={fmtKwh(summary.totals.external_in_kwh)} />
                  <Stat label="Bleed (BESS → grid)"           value={fmtKwh(summary.totals.external_out_kwh)} />
                </StatGroup>

                <StatGroup label="Net over the simulated window">
                  <Stat label="Total grid import"      value={fmtKwh(summary.totals.grid_in_kwh)} />
                  <Stat label="Total grid export"      value={fmtKwh(summary.totals.grid_out_kwh)} />
                  <Stat label="Net stored (SOC delta)" value={fmtKwh(summary.totals.soc_delta_kwh)} />
                  <Stat label="Roundtrip loss"         value={fmtKwh(summary.totals.roundtrip_loss_kwh)} highlight={COLOR_ORANGE}
                        note={`${fmtKwh(summary.totals.conversion_loss_kwh)} conversion · ${fmtKwh(summary.totals.clipping_loss_kwh)} SOC clipping`} />
                  <Stat label="Roundtrip efficiency"   value={fmtPct(summary.totals.roundtrip_efficiency_pct)} highlight={COLOR_GREEN}
                        note={`${fmtPct(summary.totals.nominal_roundtrip_pct)} nominal — gap is energy refused at 0 / 100 % SOC`} />
                </StatGroup>

                {summary.cycles && (
                  <StatGroup
                    label={`Cycling — equivalent full cycles of ${fmtKwh(summary.cycles.capacity_kwh)} `
                      + `over ${fmtNum(summary.cycles.span_days, 1)} days`}>
                    <Stat label="Total cycles"  value={fmtNum(summary.cycles.total, 2)}     highlight={COLOR_GREEN}
                          note={`${fmtNum(summary.cycles.freqreg_total, 2)} from frequency duty alone`} />
                    <Stat label="Avg per day"   value={fmtNum(summary.cycles.per_day, 3)}
                          note={`${fmtNum(summary.cycles.freqreg_per_day, 3)} duty only`} />
                    <Stat label="Avg per week"  value={fmtNum(summary.cycles.per_week, 2)}
                          note={`${fmtNum(summary.cycles.freqreg_per_week, 2)} duty only`} />
                    <Stat label="Avg per month" value={fmtNum(summary.cycles.per_month, 2)}
                          note={`${fmtNum(summary.cycles.freqreg_per_month, 2)} duty only`} />
                    <Stat label="Annualised"    value={fmtNum(summary.cycles.per_year, 1)}
                          note={`${fmtNum(summary.cycles.freqreg_per_year, 1)} duty only`} />
                  </StatGroup>
                )}

                {summary.data_quality && (summary.data_quality.clamped_steps > 0
                  || summary.data_quality.legacy_rows > 0) && (
                  <div style={{
                    margin: '8px 4px 2px', padding: '6px 9px', fontSize: 10.5, lineHeight: 1.5,
                    color: PANEL_TEXT_DIM, background: 'rgba(255,120,10,0.07)',
                    border: `1px solid ${COLOR_ORANGE}44`, borderRadius: 3,
                  }}>
                    <b style={{ color: COLOR_ORANGE }}>Integration basis</b> —{' '}
                    {summary.data_quality.legacy_rows > 0 ? (
                      <>this run predates the per-sample integration fix and was summed at a fixed{' '}
                      {summary.data_quality.nominal_step_s} s step, which over-states every energy and
                      cycle figure. Re-run the simulation to get corrected totals.</>
                    ) : (
                      <>energy is integrated on the real interval between samples (mean{' '}
                      {fmtNum(summary.data_quality.mean_step_s, 3)} s against a nominal{' '}
                      {summary.data_quality.nominal_step_s} s).{' '}
                      {summary.data_quality.clamped_steps.toLocaleString()} intervals exceeded{' '}
                      {summary.data_quality.max_step_s} s and are treated as upstream outages, so{' '}
                      {fmtNum(summary.data_quality.unobserved_hours, 1)} h of the{' '}
                      {fmtNum(summary.data_quality.window_hours, 0)} h window carries no energy and no
                      cycling ({fmtNum(summary.data_quality.coverage_pct, 2)}% coverage).</>
                    )}
                  </div>
                )}

                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: PANEL_TEXT_DIM, borderBottom: `1px solid ${PANEL_BORDER}` }}>
                      <th style={th}>{period}</th>
                      <th style={thR}>Reg in</th>
                      <th style={thR}>Reg out</th>
                      <th style={thR}>Ext in</th>
                      <th style={thR}>Ext out</th>
                      <th style={thR}>Loss</th>
                      <th style={thR}>Cycles</th>
                      <th style={thR}>SOC avg</th>
                      <th style={thR}>SOC min</th>
                      <th style={thR}>SOC max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.periods.map(p => (
                      <tr key={p.period_start} style={{ borderBottom: `1px solid ${PANEL_BORDER}` }}>
                        <td style={td}>{p.period_start.slice(0, period==='year'?4:period==='month'?7:10)}</td>
                        <td style={tdR}>{fmtKwh(p.freqreg_grid_in_kwh)}</td>
                        <td style={tdR}>{fmtKwh(p.freqreg_grid_out_kwh)}</td>
                        <td style={tdR}>{fmtKwh(p.external_in_kwh)}</td>
                        <td style={tdR}>{fmtKwh(p.external_out_kwh)}</td>
                        <td style={{ ...tdR, color: COLOR_ORANGE }}>{fmtKwh(p.roundtrip_loss_kwh)}</td>
                        <td style={{ ...tdR, fontWeight: 600 }}>{fmtNum(p.cycles_equivalent, 2)}</td>
                        <td style={tdR}>{fmtPct(p.soc_pct_avg)}</td>
                        <td style={tdR}>{fmtPct(p.soc_pct_min)}</td>
                        <td style={tdR}>{fmtPct(p.soc_pct_max)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!summary && <CenecLoadingBlock message="Loading summary…" />}
          </Panel>
        </>
      )}

      {!simExists && !running && (
        <div style={{ padding: 12, color: PANEL_TEXT_DIM, fontSize: 12 }}>
          No simulation yet. Set the configuration above and click <b>Run</b>.
        </div>
      )}
      {running && (
        <div style={{
          background: PANEL_BG, border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
          padding: '12px 14px', marginTop: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: PANEL_TEXT, marginBottom: 6 }}>
            <span>{progress?.message || 'Running simulation…'}</span>
            <span style={{ color: PANEL_TEXT_DIM, fontVariantNumeric: 'tabular-nums' }}>
              {progress ? `${progress.pct.toFixed(0)}%` : ''}
            </span>
          </div>
          <div style={{ height: 6, background: PANEL_BG_PLOT, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${progress?.pct || 0}%`, height: '100%', background: COLOR_GREEN,
              transition: 'width 0.4s ease-out',
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

const StatGroup = ({ label, children }) => (
  <div style={{ padding: '6px 4px 2px' }}>
    <div style={{
      fontSize: 10, color: PANEL_TEXT_DIM, textTransform: 'uppercase',
      letterSpacing: 0.6, marginBottom: 5,
    }}>{label}</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12 }}>{children}</div>
  </div>
);

const Stat = ({ label, value, highlight, note }) => (
  <div style={{
    flex: '1 1 200px', padding: 8, background: PANEL_BG_PLOT,
    border: `1px solid ${PANEL_BORDER}`, borderRadius: 3,
  }}>
    <div style={{ fontSize: 10, color: PANEL_TEXT_DIM, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2, color: highlight || PANEL_TEXT }}>{value}</div>
    {note && <div style={{ fontSize: 10, color: PANEL_TEXT_DIM, marginTop: 2 }}>{note}</div>}
  </div>
);

const th  = { textAlign: 'left',  padding: '6px 8px', fontWeight: 500, color: PANEL_TEXT_DIM };
const thR = { textAlign: 'right', padding: '6px 8px', fontWeight: 500, color: PANEL_TEXT_DIM };
const td  = { textAlign: 'left',  padding: '6px 8px', color: PANEL_TEXT };
const tdR = { textAlign: 'right', padding: '6px 8px', color: PANEL_TEXT };

export default BatterySimulationView;
